import type { AlbumDocument } from "../lib/types.ts";
import { isPortraitOrientation, renderPage, spreadIndices } from "./page-render.ts";

const FLIP_MS = 900;
/** Rigid book leaf: one eased rotation about the spine, no per-strip curl. */
const EASING = "ease";
/** Peak lift of the leaf off the book plane, at 90°. */
const LIFT_PX = 8;

export interface PreparedPageTurn {
  albumRevision: number;
  pageIndex: number;
  leftPage: HTMLElement;
  rightPage: HTMLElement;
}

function placeOver(target: HTMLElement, host: HTMLElement, spread: HTMLElement): void {
  const spreadRect = spread.getBoundingClientRect();
  const rect = host.getBoundingClientRect();
  target.style.left = `${rect.left - spreadRect.left}px`;
  target.style.top = `${rect.top - spreadRect.top}px`;
  target.style.width = `${rect.width}px`;
  target.style.height = `${rect.height}px`;
}

async function renderPageNode(
  album: AlbumDocument,
  pageIndex: number,
  side: "left" | "right",
): Promise<HTMLElement> {
  const page = document.createElement("div");
  page.className = `album-page ${side}`;
  await renderPage(page, album, pageIndex, false);
  return page;
}

async function decodePageImages(...pages: HTMLElement[]): Promise<void> {
  const images = pages.flatMap((page) => [...page.querySelectorAll("img")]);
  await Promise.all(images.map((image) => image.decode().catch(() => undefined)));
}

function clonePageForFlip(page: HTMLElement, width: number, height: number): HTMLElement {
  const clone = page.cloneNode(true) as HTMLElement;
  clone.classList.add("page-flip-page");
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;
  return clone;
}

/**
 * Book page turn: the leaf stays flat and swings 180° about the spine,
 * with the gutter shading baked into each face (CodePen "hardcover" style).
 */
export async function animateAlbumPageTurn(opts: {
  spread: HTMLElement;
  leftPage: HTMLElement;
  rightPage: HTMLElement;
  album: AlbumDocument;
  currentPage: number;
  forward: boolean;
  signal: AbortSignal;
  motionQuery: MediaQueryList;
  /** Repaint the spread while the landed leaf still covers it, so nothing snaps back. */
  commit: (prepared?: PreparedPageTurn) => Promise<void>;
}): Promise<void> {
  const {
    spread,
    leftPage,
    rightPage,
    album,
    currentPage,
    forward,
    signal,
    motionQuery,
    commit,
  } = opts;
  const portrait = isPortraitOrientation();
  const nextPage = currentPage + (forward ? 2 : -2);
  const to = spreadIndices(nextPage);

  const turningHost = forward ? rightPage : leftPage;
  const coveredHost = forward ? leftPage : rightPage;
  const turningRect = turningHost.getBoundingClientRect();
  const w = turningRect.width;
  const h = turningRect.height;

  let finishEarly = motionQuery.matches;
  const animations: Animation[] = [];
  const animationSettlements: Promise<void>[] = [];

  const cancelAnimations = () => {
    for (const animation of animations) animation.cancel();
  };
  const trackAnimation = (animation: Animation) => {
    animations.push(animation);
    animationSettlements.push(animation.finished.then(() => undefined, () => undefined));
  };
  const onAbort = () => cancelAnimations();
  const onResize = () => {
    finishEarly = true;
    cancelAnimations();
  };
  const onMotionChange = (event: MediaQueryListEvent) => {
    if (!event.matches) return;
    finishEarly = true;
    cancelAnimations();
  };

  signal.addEventListener("abort", onAbort, { once: true });
  window.addEventListener("resize", onResize);
  motionQuery.addEventListener("change", onMotionChange);

  const under = document.createElement("div");
  const shade = document.createElement("div");
  const leaf = document.createElement("div");

  try {
    if (signal.aborted) return;
    if (w < 2 || h < 2) {
      await commit();
      return;
    }

    // Render the destination spread once and reuse it for both the animation and commit.
    // The visible front face can be cloned directly; it is already rendered and decoded.
    const [targetLeftPage, targetRightPage] = await Promise.all([
      renderPageNode(album, to.left, "left"),
      renderPageNode(album, to.right, "right"),
    ]);
    await decodePageImages(turningHost, targetLeftPage, targetRightPage);
    if (signal.aborted) return;

    const prepared: PreparedPageTurn = {
      albumRevision: album.revision,
      pageIndex: nextPage,
      leftPage: targetLeftPage,
      rightPage: targetRightPage,
    };
    if (finishEarly || motionQuery.matches) {
      await commit(prepared);
      return;
    }

    const underPage = forward ? targetRightPage : targetLeftPage;
    const backPage = forward ? targetLeftPage : targetRightPage;

    // Gradient direction that starts at the hinge and fades toward the free edge.
    const frontGutter = portrait ? (forward ? 180 : 0) : forward ? 90 : 270;
    const backGutter = (frontGutter + 180) % 360;

    under.className = `page-flip-under ${forward ? "right" : "left"}`;
    under.setAttribute("aria-hidden", "true");
    placeOver(under, turningHost, spread);
    under.appendChild(clonePageForFlip(underPage, w, h));

    shade.className = `page-flip-shade ${forward ? "left" : "right"}`;
    shade.setAttribute("aria-hidden", "true");
    shade.style.setProperty("--gutter", `${backGutter}deg`);
    placeOver(shade, coveredHost, spread);
    shade.style.opacity = "0";

    leaf.className = `page-flip-leaf ${forward ? "right" : "left"}`;
    leaf.setAttribute("aria-hidden", "true");
    placeOver(leaf, turningHost, spread);
    leaf.style.setProperty("--page-w", `${w}px`);
    leaf.style.setProperty("--page-h", `${h}px`);
    leaf.style.transformOrigin = portrait
      ? forward
        ? "center top"
        : "center bottom"
      : forward
        ? "left center"
        : "right center";

    const front = document.createElement("div");
    front.className = "page-flip-face front";
    front.appendChild(clonePageForFlip(turningHost, w, h));

    const back = document.createElement("div");
    back.className = `page-flip-face back ${portrait ? "portrait" : "landscape"}`;
    back.appendChild(clonePageForFlip(backPage, w, h));

    const gloss = document.createElement("div");
    gloss.className = "page-flip-gloss";
    gloss.style.setProperty("--gutter", `${frontGutter}deg`);
    gloss.style.opacity = "0";

    leaf.append(front, back, gloss);

    turningHost.classList.add("page-flip-source");
    spread.classList.add("page-flipping");
    spread.append(under, shade, leaf);

    const axis = portrait ? "rotateX" : "rotateY";
    const end = portrait ? (forward ? 180 : -180) : forward ? -180 : 180;
    const timing: KeyframeAnimationOptions = {
      duration: FLIP_MS,
      easing: EASING,
      fill: "both",
    };

    // Track as each animation is created so a later animate() failure still leaves
    // every already-started animation reachable and its rejection handled.
    trackAnimation(
      leaf.animate(
        [
          { transform: `${axis}(0deg) translateZ(0px)` },
          { transform: `${axis}(${end / 2}deg) translateZ(${LIFT_PX}px)`, offset: 0.5 },
          { transform: `${axis}(${end}deg) translateZ(0px)` },
        ],
        timing,
      ),
    );
    // Both peak when the leaf is edge-on, since they share the timing curve.
    trackAnimation(
      gloss.animate([{ opacity: 0 }, { opacity: 0.55, offset: 0.5 }, { opacity: 0 }], timing),
    );
    trackAnimation(
      shade.animate([{ opacity: 0 }, { opacity: 0.45, offset: 0.5 }, { opacity: 0 }], timing),
    );

    // A resize or newly enabled reduced-motion preference may have arrived while
    // the destination pages were rendering, before the Animation objects existed.
    if (finishEarly || motionQuery.matches || signal.aborted) cancelAnimations();

    await Promise.all(animationSettlements);
    if (signal.aborted) return;
    await commit(prepared);
  } finally {
    signal.removeEventListener("abort", onAbort);
    window.removeEventListener("resize", onResize);
    motionQuery.removeEventListener("change", onMotionChange);
    cancelAnimations();
    leaf.remove();
    under.remove();
    shade.remove();
    turningHost.classList.remove("page-flip-source");
    spread.classList.remove("page-flipping");
  }
}
